<template>
  <span
    :class="textClass"
    class="export-next-run"
    :aria-label="`Next run: ${displayText}`"
  >
    <v-icon
      v-if="showIcon"
      :color="iconColor"
      x-small
      class="mr-1"
    >
      {{ icon }}
    </v-icon>
    {{ displayText }}
  </span>
</template>

<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
  name: 'ExportNextRunCell',

  props: {
    /** Next scheduled run date (ISO string or null) */
    nextRunDate: {
      type: String as () => string | null,
      default: null
    }
  },

  computed: {
    parsedDate(): Date | null {
      if (!this.nextRunDate) {
        return null
      }
      const date = new Date(this.nextRunDate)
      // new Date('invalid') returns Invalid Date, doesn't throw
      if (isNaN(date.getTime())) {
        return null
      }
      return date
    },

    isOverdue(): boolean {
      if (!this.parsedDate) {
        return false
      }
      return this.parsedDate.getTime() < Date.now()
    },

    isWithinOneHour(): boolean {
      if (!this.parsedDate || this.isOverdue) {
        return false
      }
      const oneHourFromNow = Date.now() + (60 * 60 * 1000)
      return this.parsedDate.getTime() <= oneHourFromNow
    },

    displayText(): string {
      if (!this.parsedDate) {
        return 'Not scheduled'
      }

      if (this.isOverdue) {
        return 'Overdue'
      }

      const now = new Date()
      const diffMs = this.parsedDate.getTime() - now.getTime()
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

      // If it's today but more than an hour away, show time
      const isToday = this.parsedDate.toDateString() === now.toDateString()
      if (isToday && !this.isWithinOneHour) {
        const hours = this.parsedDate.getHours().toString().padStart(2, '0')
        const minutes = this.parsedDate.getMinutes().toString().padStart(2, '0')
        return `Today at ${hours}:${minutes}`
      }

      // If it's tomorrow, show "Tomorrow at HH:mm"
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const isTomorrow = this.parsedDate.toDateString() === tomorrow.toDateString()
      if (isTomorrow) {
        const hours = this.parsedDate.getHours().toString().padStart(2, '0')
        const minutes = this.parsedDate.getMinutes().toString().padStart(2, '0')
        return `Tomorrow at ${hours}:${minutes}`
      }

      // Otherwise use relative time
      if (diffMinutes < 60) {
        return `In ${diffMinutes} minutes`
      } else if (diffHours < 24) {
        return `In ${diffHours} hours`
      } else {
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        return `In ${diffDays} days`
      }
    },

    textClass(): string {
      if (this.isOverdue) {
        return 'export-next-run--overdue'
      }
      if (this.isWithinOneHour) {
        return 'export-next-run--urgent'
      }
      return 'export-next-run--normal'
    },

    iconColor(): string {
      if (this.isOverdue) {
        return 'warning' // Amber/orange - informational, not critical
      }
      if (this.isWithinOneHour) {
        return 'warning' // Vuetify warning colour - will use custom CSS for dark mode
      }
      return 'grey' // Will use custom CSS for dark mode
    },

    icon(): string {
      if (this.isOverdue) {
        return 'mdi-alert-circle'
      }
      if (this.isWithinOneHour) {
        return 'mdi-clock-alert-outline'
      }
      return 'mdi-clock-outline'
    },

    showIcon(): boolean {
      return this.isOverdue || this.isWithinOneHour
    }
  }
})
</script>

<style scoped>
.export-next-run {
  font-size: var(--font-size-sm);
  display: inline-flex;
  align-items: center;
}

.export-next-run--overdue {
  color: var(--warning);
  font-weight: 500;
}

.export-next-run--overdue >>> .v-icon {
  color: var(--warning) !important;
}

.export-next-run--urgent {
  color: var(--warning);
  font-weight: 500;
}

.export-next-run--urgent >>> .v-icon {
  color: var(--warning) !important;
}

.export-next-run--normal {
  color: var(--muted-foreground);
}

.export-next-run--normal >>> .v-icon {
  color: var(--muted-foreground) !important;
}
</style>
